import { useEffect } from "react";
import { Globe, Loader2, X } from "lucide-react";

/**
 * Reusable "are you sure you want to publish this?" dialog.
 *
 * Publishing is the one action that makes content visible to real users, so it
 * is gated behind an explicit confirmation everywhere it appears. Built with the
 * same hand-rolled modal treatment used across the app (see the cancel /
 * plan-change dialogs in TierManagementCard) rather than a dialog library —
 * the project doesn't use one.
 *
 * The parent owns the API call and the loading flag: pass `isLoading` while the
 * request is in flight and keep the modal mounted until it resolves, so a
 * failure can leave the dialog open instead of falsely reporting success.
 */
export interface PublishConfirmationModalProps {
  isOpen: boolean;
  /** Dialog heading. Defaults to "Confirm Publish". */
  title?: string;
  /** Body copy above the details block. */
  message?: string;
  /** Details shown to the admin — each row is hidden when not supplied. */
  resourceName?: string | null;
  resourceType?: string | null;
  currentStatus?: string | null;
  targetAudience?: string | null;
  /** Confirm button copy, e.g. "Publish" / "Publishing…". */
  confirmLabel?: string;
  loadingLabel?: string;
  /** Disables both buttons, swaps in the spinner, and blocks dismissal. */
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const DEFAULT_MESSAGE =
  "Are you sure you want to publish this content? Once published, it will become visible to the applicable users.";

export default function PublishConfirmationModal({
  isOpen,
  title = "Confirm Publish",
  message = DEFAULT_MESSAGE,
  resourceName,
  resourceType,
  currentStatus,
  targetAudience,
  confirmLabel = "Publish",
  loadingLabel = "Publishing…",
  isLoading = false,
  onConfirm,
  onCancel,
}: PublishConfirmationModalProps) {
  // Escape closes the dialog, but never mid-request — the call is already in
  // flight and dismissing would hide its outcome.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isLoading) onCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, isLoading, onCancel]);

  if (!isOpen) return null;

  const details = [
    { label: "Title", value: resourceName },
    { label: "Resource Type", value: resourceType },
    { label: "Current Status", value: currentStatus },
    { label: "Target Audience", value: targetAudience },
  ].filter((d) => !!d.value);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget && !isLoading) onCancel(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="publish-confirm-title"
    >
      <div className="flex max-h-[92dvh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="shrink-0 border-b border-border px-6 pb-4 pt-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/10">
                <Globe className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p id="publish-confirm-title" className="text-base font-extrabold text-foreground">
                  {title}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  This makes the content live
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              aria-label="Close"
              className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto">
          <div className="px-6 pt-4">
            <p className="text-sm leading-relaxed text-foreground">{message}</p>
          </div>

          {details.length > 0 && (
            <div className="px-6 pb-2 pt-3.5">
              <dl className="divide-y divide-border/50 rounded-xl border border-border bg-muted/30 px-4">
                {details.map((d) => (
                  <div key={d.label} className="flex items-start justify-between gap-4 py-2.5">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {d.label}
                    </dt>
                    <dd className="min-w-0 text-right text-sm font-medium text-foreground">
                      {d.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>

        {/* Footer — actions stay pinned and fully visible on any screen height */}
        <div className="shrink-0 border-t border-border px-6 py-4">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="h-11 flex-1 rounded-xl border border-border text-sm font-semibold text-foreground transition-colors hover:bg-accent disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-600 text-sm font-bold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isLoading ? loadingLabel : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
